import { isInvestmentCorrectionMode } from "@/domain/investment/stageRules";
import { InvestmentState } from "@/domain/investment";
import type { InvestorSummary } from "@/domain/investor";
import type { FiscalQuota } from "@/domain/beneficiary";
import type { InvestmentFormData } from "@/domain/validation/investmentSchemas";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  investmentCapacityPreview,
  ownDeductionOf,
  quotaRoom,
} from "@/domain/fiscal/investmentCapital";
import { createInitialFormData } from "@/application/mappers/investments/investmentFormMapper";
import type { InvestmentFormSetupMessages, UseInvestmentFormProps } from "./investmentForm.types";
import { useInvestmentFormSync } from "./useInvestmentFormSync";

const EMPTY_QUOTAS: FiscalQuota[] = [];

export function useInvestmentFormSetup(
  props: UseInvestmentFormProps,
  messages: InvestmentFormSetupMessages,
) {
  const {
    form,
    validationContext,
    isOpen,
    preselectedProject = null,
    preselectedInvestor = null,
    preselectedState = InvestmentState.PROSPECT,
    existingInvestment = null,
  } = props;

  const isEditMode = !!existingInvestment;
  const isCorrectionMode = isInvestmentCorrectionMode(existingInvestment);

  const formData = form.watch();

  const updateFormData = useCallback(
    (updates: Partial<InvestmentFormData>) => {
      for (const [key, value] of Object.entries(updates)) {
        form.setValue(key as keyof InvestmentFormData, value as never, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [form],
  );

  const setFormData = useCallback((values: InvestmentFormData) => form.reset(values), [form]);

  const [selectedInvestor, setSelectedInvestor] = useState<InvestorSummary | null>(null);
  const [selectedQuota, setSelectedQuota] = useState<FiscalQuota | null>(null);

  const {
    loadingBeneficiaryDetails,
    beneficiaryDetails,
    detailError,
    resetSyncState,
    availableFiscalQuotas,
  } = useInvestmentFormSync({
    isOpen,
    isEditMode,
    preselectedProject,
    preselectedInvestor,
    preselectedState,
    existingInvestment,
    formData,
    updateFormData,
    setSelectedInvestor,
    availableFiscalQuotas: EMPTY_QUOTAS,
    selectedQuota,
    setSelectedQuota,
    setFormData,
  });

  const room = useMemo(
    () => quotaRoom(selectedQuota, formData.returnPercentage, existingInvestment),
    [selectedQuota, formData.returnPercentage, existingInvestment],
  );
  const capacity = useMemo(
    () =>
      investmentCapacityPreview(
        selectedQuota,
        formData.amount,
        formData.returnPercentage,
        existingInvestment,
      ),
    [selectedQuota, formData.amount, formData.returnPercentage, existingInvestment],
  );

  validationContext.current.hasSelectedQuota = !!selectedQuota;
  validationContext.current.deductionAvailable = selectedQuota?.money.deduction.available ?? null;
  validationContext.current.ownDeduction = ownDeductionOf(existingInvestment);

  useEffect(() => {
    if (form.getValues("amount")) form.trigger("amount");
  }, [room, form]);

  const { loadingPromoterDetails } = messages;
  useEffect(() => {
    if (detailError) form.setError("root", { message: loadingPromoterDetails });
  }, [detailError, form, loadingPromoterDetails]);

  const resetForm = useCallback(() => {
    form.reset(createInitialFormData(preselectedProject, preselectedInvestor, preselectedState));
    setSelectedInvestor(preselectedInvestor || null);
    setSelectedQuota(null);
    resetSyncState();
  }, [form, preselectedProject, preselectedInvestor, preselectedState, resetSyncState]);

  return {
    isEditMode,
    isCorrectionMode,
    form,
    selectedInvestor,
    setSelectedInvestor,
    selectedQuota,
    loadingBeneficiaryDetails,
    beneficiaryDetails,
    availableFiscalQuotas,
    capacity,
    resetForm,
  };
}
